import { useMemo, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import styles from "./SupplierManagement.module.css";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import SupplierDetail from "../SupplierDetail/SupplierDetail";
import AddSupplierModal from "../../../components/Suppliers/AddSupplierModal";
import { useToast } from "../../../context/ToastContext";
import {
  useCreateSupplier,
  useExportSuppliers,
  useSuppliersList,
} from "../../../api/suppliers/useSuppliersApi";

const SupplierManagement = () => {
  const { showToast } = useToast();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [vatRoleFilter, setVatRoleFilter] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const {
    data: suppliers,
    loading: suppliersLoading,
    error: suppliersError,
    refetch,
  } = useSuppliersList({
    search: searchTerm.trim(),
    status: statusFilter,
    type: typeFilter,
    country: countryFilter,
    vatRole: vatRoleFilter,
  });

  const createSupplier = useCreateSupplier();
  const exportSuppliers = useExportSuppliers();

  const supplierList = useMemo(
    () => (Array.isArray(suppliers) ? suppliers : []),
    [suppliers]
  );

  const countries = useMemo(() => {
    const set = new Set();
    supplierList.forEach((supplier) => {
      if (supplier?.country) set.add(supplier.country);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [supplierList]);

  const vatRoles = useMemo(() => {
    const set = new Set();
    supplierList.forEach((supplier) => {
      if (supplier?.vatRole) set.add(supplier.vatRole);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [supplierList]);

  const handleExport = async () => {
    try {
      const blob = await exportSuppliers.execute("csv");
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "suppliers.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      showToast("Suppliers exported successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to export suppliers", "error");
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setSearchTerm(searchInput.trim());
  };

  const handleRefresh = async () => {
    const result = await refetch();
    if (result?.success === false) {
      showToast(result.message || "Failed to refresh suppliers", "error");
    } else {
      showToast("Supplier list refreshed", "success");
    }
  };

  const handleAddSupplier = () => {
    setIsAddModalOpen(true);
  };

  const handleView = (supplier) => {
    setSelectedSupplier(supplier);
  };

  const handleCreateSupplier = async (payload) => {
    try {
      await createSupplier.execute(payload);
      setIsAddModalOpen(false);
      refetch();
      showToast("Supplier created successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to create supplier", "error");
      throw err;
    }
  };

  const handleCloseDrawer = () => {
    setSelectedSupplier(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Supplier Management</h1>
          <p className={styles.subtitle}>Manage API and offline suppliers</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.exportBtn} onClick={handleExport}>
            <Download size={16} className={styles.icon} />
            {exportSuppliers.loading ? "Exporting..." : "Export"}
          </button>
          <button className={styles.refreshBtn} onClick={handleRefresh}>
            <RefreshCw size={16} className={styles.icon} />
            {suppliersLoading ? "Refreshing..." : "Refresh"}
          </button>
          <button className={styles.addBtn} onClick={handleAddSupplier}>
            + Add Supplier
          </button>
        </div>
      </div>
      <div className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by ID, code, or name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchBtn}>
            Search
          </button>
        </form>

        <SelectBox
          placeholder="All Status"
          value={statusFilter}
          options={[
            { label: "All Status", value: "" },
            { label: "Active", value: "Active" },
            { label: "Inactive", value: "Inactive" },
            { label: "Pending", value: "Pending" },
          ]}
          onChange={(e) => setStatusFilter(e.target.value)}
        />

        <SelectBox
          placeholder="All Types"
          value={typeFilter}
          options={[
            { label: "All Types", value: "" },
            { label: "API", value: "API" },
            { label: "Hybrid", value: "Hybrid" },
            { label: "Offline", value: "Offline" },
          ]}
          onChange={(e) => setTypeFilter(e.target.value)}
        />

        <SelectBox
          placeholder="All Countries"
          value={countryFilter}
          options={[
            { label: "All Countries", value: "" },
            ...countries.map((country) => ({ label: country, value: country })),
          ]}
          onChange={(e) => setCountryFilter(e.target.value)}
        />

        <SelectBox
          placeholder="All VAT Roles"
          value={vatRoleFilter}
          options={[
            { label: "All VAT Roles", value: "" },
            ...vatRoles.map((role) => ({ label: role, value: role })),
          ]}
          onChange={(e) => setVatRoleFilter(e.target.value)}
        />
      </div>

      {suppliersError && (
        <div style={{ color: "#b91c1c", marginBottom: "12px", fontSize: "13px" }}>{suppliersError}</div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Supplier ID</th>
              <th>Code</th>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Country</th>
              <th>VAT Role</th>
              <th>Contact Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {suppliersLoading ? (
              <tr>
                <td colSpan={9} style={{ textAlign: "center" }}>
                  Loading suppliers...
                </td>
              </tr>
            ) : supplierList.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: "center" }}>
                  No suppliers found.
                </td>
              </tr>
            ) : (
              supplierList.map((supplier) => (
                <tr key={supplier.id}>
                  <td className={styles.supplierId}>{supplier.supplierId || supplier.id}</td>
                  <td className={styles.code}>{supplier.code}</td>
                  <td className={styles.name}>{supplier.name}</td>
                  <td>
                    <span className={`${styles.badge} ${styles[`badge${supplier.type}`]}`}>
                      {supplier.type}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.badge} ${styles[`badge${supplier.status}`]}`}>
                      {supplier.status}
                    </span>
                  </td>
                  <td>{supplier.country || "-"}</td>
                  <td>{supplier.vatRole || "-"}</td>
                  <td className={styles.email}>{supplier.contactEmail || "-"}</td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleView(supplier)}
                        title="View"
                      >
                        👁
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedSupplier && (
        <SupplierDetail
          supplier={selectedSupplier}
          onClose={handleCloseDrawer}
          onSaved={() => {
            refetch();
          }}
        />
      )}
      <AddSupplierModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleCreateSupplier}
        submitting={createSupplier.loading}
      />
    </div>
  );
};

export default SupplierManagement;
